import { ShieldAlert } from 'lucide-react';
import { Button } from './ui';
import LegalLinks from './LegalLinks';
import { useTranslation } from '../i18n';
import { getAgeGateExitUrl } from '../security/ageGateStorage';

export interface AgeGateProps {
  onConfirm: () => void;
  /** Overridable for tests; defaults to leaving for `VITE_AGE_GATE_EXIT_URL`. */
  onLeave?: () => void;
}

function leaveToNeutralPage(): void {
  window.location.replace(getAgeGateExitUrl());
}

/**
 * P1-S4 — shown once per device, before any page except help and the decoy
 * notes, to signed-in and signed-out visitors alike. It is independent of the
 * PIN lock. "Leave" goes to a neutral external page so nothing about the app
 * stays in front of someone under 18.
 */
export default function AgeGate({
  onConfirm,
  onLeave = leaveToNeutralPage,
}: AgeGateProps) {
  const { t } = useTranslation();

  return (
    <main
      aria-labelledby="age-gate-title"
      aria-describedby="age-gate-body"
      className="flex min-h-dvh flex-col items-center justify-center gap-6 px-6 py-10 text-center"
    >
      <span
        aria-hidden="true"
        className="flex h-16 w-16 items-center justify-center rounded-full bg-hc-accent/15 text-hc-accent-soft"
      >
        <ShieldAlert className="h-8 w-8" />
      </span>
      <div className="flex max-w-sm flex-col gap-3">
        <h1
          id="age-gate-title"
          className="font-display text-3xl leading-tight text-white"
        >
          {t('ageGate.title')}
        </h1>
        <p id="age-gate-body" className="text-base text-white/70">
          {t('ageGate.body')}
        </p>
      </div>
      <div className="flex w-full max-w-sm flex-col gap-3">
        <Button size="lg" fullWidth autoFocus onClick={onConfirm}>
          {t('ageGate.confirm')}
        </Button>
        <Button variant="secondary" size="lg" fullWidth onClick={onLeave}>
          {t('ageGate.leave')}
        </Button>
      </div>
      <LegalLinks />
    </main>
  );
}
